import { HTMLAttributes, ReactNode, SyntheticEvent } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete, { AutocompleteRenderOptionState } from '@mui/material/Autocomplete';
import Loader from '../../../common/Loader/Loader';
import { useAttributeLabel } from '../../../../lib/hooks/labels';
import { CY_SEARCH_AND_SORT } from '../../../../lib/constants/cypress';
import { ATTRIBUTES } from '../../../../lib/constants/attributes';
import { Label } from '../../../common/Label/Label';
import { SCORM_LABELS } from '../../../../lib/constants/labelKeys';
import styles from './ControlledAutocomplete.module.scss';

export type AutocompleteOption<T = string> = { label: T; id: string };

type ControlledAutocompleteProps<T> = {
  options: AutocompleteOption<T>[];
  inputValue: string;
  onChange: (option: string) => void;
  onOptionChange: (option: AutocompleteOption<T> | null) => void;
  placeholderKey: string;
  disabled?: boolean;
  value: AutocompleteOption<T> | null;
  isLoading?: boolean;
  groupBy?: (option: AutocompleteOption<T>) => string;
} & (T extends string
  ? { renderOption: never; getOptionLabel: never }
  : {
      renderOption: (
        props: HTMLAttributes<HTMLLIElement>,
        option: AutocompleteOption<T>,
        state: AutocompleteRenderOptionState,
      ) => ReactNode;
      getOptionLabel: (option: AutocompleteOption<T>) => string;
    });

const ControlledAutocomplete = <T extends any = string>({
  onChange,
  onOptionChange,
  placeholderKey,
  isLoading,
  ...props
}: ControlledAutocompleteProps<T>) => {
  const placeholder = useAttributeLabel(ATTRIBUTES.PLACEHOLDER, placeholderKey);

  const handleInputChange = (_: SyntheticEvent<Element, Event>, value: string) => {
    onChange(value);
  };

  const handleOptionChange = (_: SyntheticEvent<Element, Event>, option: AutocompleteOption<T> | null) => {
    onOptionChange(option);
  };

  return (
    <Autocomplete<AutocompleteOption<T>>
      selectOnFocus
      clearOnBlur
      onInputChange={handleInputChange}
      onChange={handleOptionChange}
      filterOptions={options => options}
      classes={{
        inputRoot: styles.inputRoot,
        input: styles.input,
        option: styles.option,
        popupIndicator: styles.popupIndicator,
        clearIndicator: styles.popupIndicator,
        noOptions: styles.noOptions,
        groupLabel: styles.groupLabel,
      }}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      renderInput={({ InputProps, inputProps, ...params }) => (
        <TextField
          InputProps={{
            inputProps: { 'data-cy': CY_SEARCH_AND_SORT.SEARCH_INPUT, ...inputProps },
            ...InputProps,
            endAdornment: isLoading && <Loader containerProps={{ className: styles.loader }} />,
          }}
          {...params}
          {...placeholder}
        />
      )}
      noOptionsText={<Label name={SCORM_LABELS.CONNECT_DIALOG.NO_FILES_TEXT} />}
      {...props}
    />
  );
};

export default ControlledAutocomplete;
